import React from 'react';
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
  useParams,
} from 'react-router-dom';
import '@/styles/reduction.scss';
import { useAuthState } from './utils/auth';

import {
  CompanyFilterContext,
  ManageFilterOptions,
  useFilterLoading,
  useCompanyNameToId,
  useCompanySlugToName,
} from '@/hooks/useFilter';
import {
  ManageProjectOptions,
  useProjectSlugToName,
} from '@/hooks/useProjectOptions';
import { RedirectMachinists } from '@/modules/app/RedirectMachinists';
import { RegisterSentryUser } from '@/utils/RegisterSentryUser';
import { EmailChangeDialog } from '@/modules/email_change/EmailChangeDialog';
import { RegisterPostHogUser } from '@/utils/RegisterPostHogUser';
import { ContextRestrictedLayoutRoute } from './components/Layout/ContextRestrictedLayoutRoute';
import LayoutRoute from '@/components/Layout/LayoutRoute';
import MainLayout from '@/components/Layout/MainLayout';
import MaybeMaintenanceMode from '@/components/MaybeMaintenanceMode';
import { JDOauthCallbackHandler } from '@/modules/telemetry/JohnDeereAuth';
import { CaseOauthCallbackHandler } from '@/modules/telemetry/CaseAuth';
import { MaybeUnpaidNoticePage } from '@/components/UnpaidNoticePage';
import {
  companyHiddenRoutesDef,
  companyNavRoutesDef,
  globalNavRoutesDef,
  globalHiddenRroutesDef,
} from '@/modules/app/routes';
import { getRouteSpecificity, useRoutes } from '@/hooks/useRoutes';
import { SentryTracedRoute } from '@/modules/app/SentryRoute';
import { ConfirmModalToggleProvider } from '@/components/Modals/ConfirmUnsavedDataModal';
import { BilderitLogoHorizontal } from '@/components/BilderitLogoHorizontal';
import { LoadingMessageCard } from '@/components/LoadingMessageCard';
import { toast } from 'sonner';

// pages
const AuthPage = React.lazy(() => import('@/modules/app/AuthPage'));
// const ComingSoonPage = React.lazy(() => import('@/components/Layout/ComingSoon'));

const App: React.FC = () => {
  const navRoutes = useRoutes(globalNavRoutesDef);
  const hiddenRoutes = useRoutes(globalHiddenRroutesDef);
  const globalRoutes = [...navRoutes, ...hiddenRoutes].sort(
    (a, b) => getRouteSpecificity(b.path) - getRouteSpecificity(a.path)
  );

  const companyNavRoutes = useRoutes(companyNavRoutesDef);
  const companyHiddenRoutes = useRoutes(companyHiddenRoutesDef);
  const companyRoutes = [...companyNavRoutes, ...companyHiddenRoutes].sort(
    (a, b) => getRouteSpecificity(b.path) - getRouteSpecificity(a.path)
  );

  return (
    <>
      <ManageFilterOptions />
      <ManageProjectOptions />
      <React.Suspense
        fallback={
          <div className="flex min-h-svh flex-col items-center justify-center gap-6 bg-sidebar p-6 md:p-10">
            <div className="flex w-full max-w-sm flex-col gap-6">
              <a
                href="/"
                className="flex items-center gap-2 self-center font-medium"
              >
                <BilderitLogoHorizontal className="h-12" />
              </a>
              <LoadingMessageCard message="Cargando..." />
            </div>
          </div>
        }
      >
        <ConfirmModalToggleProvider>
          <AuthPage>
            <Switch>
              <SentryTracedRoute
                path="/oauth-jd-callback"
                exact
                strict
                render={() => <JDOauthCallbackHandler />}
              />
              <SentryTracedRoute
                path="/oauth-case-callback"
                exact
                strict
                render={() => <CaseOauthCallbackHandler />}
              />
              {/* trackPageview={false}: this catch-all only hosts the
                  screen routes below (LayoutRoute / ContextRestricted-
                  LayoutRoute, both traced). Left on, it would capture every
                  pathname first — including the redirect-only entries that
                  opt out — because the per-pathname dedupe lets the first
                  capture win. */}
              <SentryTracedRoute
                path="*"
                exact
                strict
                trackPageview={false}
                render={() => (
                  <RedirectMachinists>
                    <RegisterSentryUser />
                    <RegisterPostHogUser scope="global" />
                    <EmailChangeDialog />

                    <MaybeMaintenanceMode>
                      <HasCompany>
                        <Switch>
                          {globalRoutes.flatMap((route) => (
                            <LayoutRoute
                              key={route.name}
                              path={route.path}
                              exact={route.exact}
                              trackPageview={route.trackPageview}
                              layout={route.layout ?? MainLayout}
                              component={
                                (route.component
                                  ? route.component
                                  : () => <div>Coming Soon</div>) as any
                              }
                              title={route.name}
                              breadCrumbs={route.breadcrumbs}
                              rbacAction={route.permission}
                            />
                          ))}
                          {/* <Route path="/:company?/:project?"> */}
                          <CompanyFilterContext>
                            {/* The company-scoped registration: only a
                                mount inside CompanyFilterContext sees the
                                selected company (scoped filterAtom), so
                                this is the one that groups and writes the
                                company person properties. The global one
                                above only identifies. */}
                            <RegisterPostHogUser />
                            <MaybeUnpaidNoticePage>
                              <Switch>
                                {companyRoutes.flatMap((route) => (
                                  <ContextRestrictedLayoutRoute
                                    key={route.name}
                                    path={route.path}
                                    exact={route.exact}
                                    layout={route.layout ?? MainLayout}
                                    component={
                                      (route.component
                                        ? route.component
                                        : () => <div>Coming Soon</div>) as any
                                    }
                                    title={route.name}
                                    breadCrumbs={route.breadcrumbs}
                                    rbacAction={route.permission}
                                  />
                                ))}
                                <Route path="/:company?/:project?/*">
                                  <ValidateCompanyProjectSlugs />
                                </Route>
                              </Switch>
                            </MaybeUnpaidNoticePage>
                          </CompanyFilterContext>
                          {/* </Route> */}
                        </Switch>
                      </HasCompany>
                    </MaybeMaintenanceMode>
                  </RedirectMachinists>
                )}
              />
            </Switch>
          </AuthPage>
        </ConfirmModalToggleProvider>
      </React.Suspense>
    </>
  );
};

const HasCompany: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { hasCompany } = useAuthState();
  if (!hasCompany && location.pathname !== '/create') {
    // If the user doesn't have a company yet, make him choose or create one
    return <Redirect to={'/create'} />;
  } else {
    return <>{children}</>;
  }
};

// If we get to the bottom of the routing tree, something is wrong with the company/project slugs
// redirect to the base of the company or project
const ValidateCompanyProjectSlugs = () => {
  const filterLoading = useFilterLoading();
  const companySlugToName = useCompanySlugToName();
  const companyNameToId = useCompanyNameToId();
  const projectSlugToName = useProjectSlugToName();
  const params = useParams<{ company?: string; project?: string }>();
  const history = useHistory();
  React.useEffect(() => {
    if (filterLoading) return;
    const companySlug = params.company;
    const projectSlug = params.project;
    if (companySlug) {
      try {
        const companyId = companyNameToId(companySlugToName(companySlug));
        if (projectSlug) {
          try {
            projectSlugToName(projectSlug, companyId);
            toast.error(
              'URL no válida. Redirigiendo al dashboard de proyecto...',
              {
                duration: 4000,
              }
            );
            // Both slugs are valid, but we still got here, something is wrong
            history.replace(`/${companySlug}/${projectSlug}`);
          } catch {
            toast.error(
              'URL no válida. Redirigiendo al dashboard de la compañía...',
              {
                duration: 4000,
              }
            );
            // Invalid project slug
            history.replace(`/${companySlug}`);
          }
        }
      } catch {
        toast.error('URL no válida. Elegir una compañía...', {
          duration: 4000,
        });
        // Invalid company slug
        history.replace(`/choose_company`);
      }
    }
  }, [
    companySlugToName,
    history,
    projectSlugToName,
    params.company,
    params.project,
    companyNameToId,
    params,
    filterLoading,
  ]);
  return null;
};

// export default App;
export default App;
